import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import {
  Plus, Search, Edit, Trash2, Activity, MapPin, X, Save, Layers
} from 'lucide-react';
import { useFontSize } from './FontSizeContext';

const FeatureMaster = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [features, setFeatures] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    feature_id: '',
    description: '',
    type: 'POINT'
  });

  // 1. Fetch Data
  const fetchFeatures = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/features-master', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFeatures(res.data.data || []);
    } catch (err) {
      toast.error(t('toasts.load_fail', 'Failed to load features'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // 2. Filter Data
  const filteredFeatures = features.filter(f => 
    f.feature_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (f.description && f.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // 3. Handlers
  const handleOpenModal = (feature = null) => {
    if (feature) {
      setFormData(feature);
      setIsEditing(true);
    } else {
      setFormData({ feature_id: '', description: '', type: 'POINT' });
      setIsEditing(false);
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.feature_id) {
      return toast.error(t('features.feature_id', 'Feature ID') + ' is required');
    }

    const loadId = toast.loading(t('workflow.loading', 'Saving...'));
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/features-master/save', formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(t('toasts.save_success', 'Saved successfully'), { id: loadId });
      setIsModalOpen(false);
      fetchFeatures();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toasts.save_fail', 'Save failed'), { id: loadId });
    }
  };

  const handleDelete = async (featureId) => {
    // 🎯 استخدام SweetAlert2 بدلاً من النوافذ المنبثقة التقليدية
    const result = await Swal.fire({
      title: t('swal.delete_title', 'Are you sure?'),
      text: `${t('swal.delete_text', 'You are about to delete')} ${featureId}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.delete', 'Delete'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      customClass: {
        popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]`
      }
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/features-master/${featureId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(t('toasts.delete_success', 'Deleted successfully'));
        fetchFeatures();
      } catch (err) {
        toast.error(err.response?.data?.detail || t('features.error_in_use', 'Cannot delete feature'));
      }
    }
  };

  return (
    <div className={`p-6 bg-[#f3f4f6] min-h-screen ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'} text-[12px]`} style={{ fontSize: `${fontSize}px` }}>
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header Area */}
        <div className="flex justify-between items-end border-b-2 border-slate-200 pb-4">
          <div>
            <h3 className="text-2xl font-black text-[#1a3353] flex items-center gap-3 uppercase tracking-tighter">
              <Layers size={28} className="text-orange-500" /> 
              {t('features.title', 'Linear Feature Catalog')}
            </h3>
            <p className="text-slate-400 font-bold mt-1 uppercase tracking-widest text-[10px]">
              Define point and linear segment types for pipeline assets
            </p>
          </div>
          <button 
            onClick={() => handleOpenModal()}
            className="bg-[#1a3353] hover:bg-orange-600 text-white px-6 py-2 rounded-sm font-black flex items-center gap-2 transition-all shadow-md uppercase text-[11px]"
          >
            <Plus size={16} /> {t('common.add_new', 'Add New Feature')}
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white p-3 rounded-md shadow-sm border border-slate-200 flex items-center gap-3 w-full md:w-1/3">
          <Search size={16} className="text-slate-400" />
          <input 
            type="text" 
            placeholder={t('common.search', 'Search...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full outline-none bg-transparent font-bold text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}
          />
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded shadow-sm overflow-hidden">
          <table className={`w-full text-left ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-[#f8fafc] border-b text-slate-500 font-black uppercase text-[10px] tracking-widest">
              <tr>
                <th className="p-4 border-r w-40">{t('features.feature_id', 'Feature Code')}</th>
                <th className="p-4 border-r">{t('features.description', 'Description')}</th>
                <th className="p-4 border-r w-48 text-center">{t('features.type', 'Type')}</th>
                <th className="p-4 w-20 text-center">{t('common.action', 'Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="4" className="p-8 text-center text-slate-400 font-bold animate-pulse">
                    {t('workflow.loading', 'Loading data...')}
                  </td>
                </tr>
              ) : filteredFeatures.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-8 text-center text-slate-400 italic">
                    {t('common.no_records', 'No records found.')}
                  </td>
                </tr>
              ) : (
                filteredFeatures.map((feat) => (
                  <tr key={feat.feature_id} className="hover:bg-blue-50/50 transition-colors group">
                    <td className="p-4 border-r font-black text-blue-900">{feat.feature_id}</td>
                    <td className="p-4 border-r font-bold text-slate-600">{feat.description || '---'}</td>
                    <td className="p-4 border-r text-center">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                        feat.type === 'POINT' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {feat.type === 'POINT' ? <MapPin size={12}/> : <Activity size={12}/>}
                        {t(`features.${feat.type.toLowerCase()}`, feat.type)}
                      </span>
                    </td>
                    <td className="p-4 flex items-center justify-center gap-3">
                      <button onClick={() => handleOpenModal(feat)} className="text-slate-400 hover:text-blue-600 transition-colors">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDelete(feat.feature_id)} className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      {/* Modal / Dialog */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-lg shadow-2xl overflow-hidden animate-in zoom-in-95">
            <div className="bg-[#1a3353] p-4 flex justify-between items-center text-white">
              <h3 className="font-black uppercase tracking-widest">
                {isEditing ? t('common.edit', 'Edit Feature') : t('common.add_new', 'Add New Feature')}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="hover:text-orange-500 transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-6">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-slate-500">{t('features.feature_id', 'Feature Code')} <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={formData.feature_id}
                  onChange={(e) => setFormData({...formData, feature_id: e.target.value.toUpperCase()})}
                  disabled={isEditing}
                  className="w-full border-b-2 border-slate-200 py-2 outline-none focus:border-blue-600 font-bold uppercase text-blue-900 disabled:opacity-50"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-slate-500">{t('features.description', 'Description')}</label>
                <input 
                  type="text" 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className={`w-full border-b-2 border-slate-200 py-2 outline-none focus:border-blue-600 font-bold text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-slate-500">{t('features.type', 'Type')} <span className="text-red-500">*</span></label>
                <select 
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className={`w-full border-b-2 border-slate-200 py-2 outline-none focus:border-blue-600 font-bold text-slate-700 cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <option value="POINT">{t('features.point', 'Point (e.g. Valve)')}</option>
                  <option value="LINEAR">{t('features.linear', 'Linear Segment (e.g. Material)')}</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-2 rounded-sm font-bold text-slate-500 hover:bg-slate-100 transition-colors uppercase text-[10px]"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-2 rounded-sm font-black text-white bg-green-600 hover:bg-green-700 flex items-center gap-2 transition-all uppercase text-[10px] shadow-md"
                >
                  <Save size={14} /> {t('common.save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureMaster;